export type MagicLinkDisplayFallback = {
  name?: string;
  email: string;
};

/** Validated ?user= payload — email is guaranteed; other fields pass through for verifyToken. */
export type MagicLinkUserPayload = Record<string, unknown> & { email: string };

export type ParseMagicLinkUserResult =
  | { valid: true; user: MagicLinkUserPayload; displayFallback: MagicLinkDisplayFallback }
  | { valid: false };

/**
 * Decodes and validates the ?user= payload from a magic-link sign-in URL.
 * Rejects anything that isn't a non-null object with a non-empty string email.
 */
export function parseMagicLinkUser(encryptedUser: string): ParseMagicLinkUserResult {
  const userStr = atob(encryptedUser);
  if (!userStr) {
    return { valid: false };
  }

  let userObj: unknown;
  try {
    userObj = JSON.parse(userStr);
  } catch {
    return { valid: false };
  }

  if (
    userObj &&
    typeof userObj === "object" &&
    typeof (userObj as { email?: unknown }).email === "string" &&
    (userObj as { email: string }).email.length > 0
  ) {
    const parsed = userObj as MagicLinkUserPayload & { firstName?: unknown };
    const firstName =
      typeof parsed.firstName === "string" && parsed.firstName ? parsed.firstName : undefined;

    return {
      valid: true,
      user: parsed,
      displayFallback: { name: firstName, email: parsed.email },
    };
  }

  return { valid: false };
}

/** sessionStorage flag: user chose Sign out — do not SSO-bounce via accounts. */
export const EXPLICIT_LOGOUT_SESSION_KEY = "iq-explicit-logout";

/** Record that the user signed out of iQ (survives until tab close). */
export function markExplicitLogout(): void {
  try {
    sessionStorage.setItem(EXPLICIT_LOGOUT_SESSION_KEY, "1");
  } catch {
    // Ignore private-mode / blocked storage.
  }
}

export function isExplicitLogout(): boolean {
  try {
    return sessionStorage.getItem(EXPLICIT_LOGOUT_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearExplicitLogout(): void {
  try {
    sessionStorage.removeItem(EXPLICIT_LOGOUT_SESSION_KEY);
  } catch {
    // Ignore.
  }
}
